// PaymentPlanPage — /send/payment-plan route: send the standalone Security
// Deposit Payment Plan agreement (WEL-77) to a unit's CURRENT residents.
//
// Flow (modeled on RenewalPage):
//   1. Pick a property → unit.
//   2. "Load current residents" pulls the unit's current financially-
//      responsible residents from AppFolio (listCurrentResidents).
//   3. The roster is EDITABLE: staff add / remove / edit names + emails — these
//      are the residents who'll sign (Miles countersigns last, server-side).
//   4. Enter the plan numbers — Principal ($), Number of payments (2–6), Start
//      date — and a live schedule preview (Charge Date | Amount) renders
//      client-side with the SAME cents math the backend sends.
//   5. "Send payment plan" → POST /api/payment-plan/initiate.
//
// The feature is DORMANT by default (a legal document). When the backend's
// PAYMENT_PLAN_SEND_ENABLED flag is off, the initiate route answers 503 and we
// show a friendly "not enabled yet" state.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchSelect } from '@/components/SearchSelect';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import {
  listCurrentResidents,
  listProperties,
  listUnits,
  type PropertyOption,
  type RenewalResidentOption,
  type UnitOption,
  unitOptionLabel,
} from '@/hooks/useLeaseLifecycle';
import { formatMoneyCents, parseMoneyDollarsToCents } from '@/lib/money';
import { isEmptySendContext, sendContextFromSearch } from '@/lib/sendContext';
import {
  PAYMENT_PLAN_MAX_PAYMENTS,
  PAYMENT_PLAN_MIN_PAYMENTS,
  canSendPaymentPlan,
  computePaymentScheduleCents,
  initiatePaymentPlan,
  isResidentEmailInvalid,
  isResidentRowValid,
  validatePaymentPlanInput,
  PaymentPlanNotEnabledError,
  type PaymentPlanPrincipalType,
  type PaymentPlanResidentDraft,
} from '@/lib/paymentPlanClient';

interface ResidentRow extends PaymentPlanResidentDraft {
  key: number;
  /** Verbatim AppFolio TenantType, for the roster badge (display only). */
  tenantType: string | null;
}

let nextKey = 1;
const blankRow = (): ResidentRow => ({
  key: nextKey++,
  tenantId: '',
  firstName: '',
  lastName: '',
  email: '',
  tenantType: null,
});

const fromAppFolio = (r: RenewalResidentOption): ResidentRow => ({
  key: nextKey++,
  tenantId: r.tenantId,
  firstName: r.firstName,
  lastName: r.lastName,
  email: r.email ?? '',
  tenantType: r.tenantType,
});

/** Short label for the AppFolio TenantType, for the roster badge. */
function tenantTypeLabel(type: string | null): string | null {
  if (!type) return null;
  const t = type.trim().toLowerCase();
  if (t === 'co-signer' || t === 'cosigner') return 'Cosigner';
  if (t === 'financially responsible') return 'Resident';
  return type;
}

/** Format a yyyy-mm-dd as a friendly local date for the schedule preview. */
function formatChargeDate(ymd: string): string {
  const d = new Date(ymd + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function PaymentPlanPage(): React.JSX.Element {
  // The household this page was opened FOR, when it was reached from a
  // household row's "Send…" rather than from the nav (sol finding 10). It only
  // ever SEEDS the two pickers — read once, on mount, so a staffer who then
  // picks a different unit is not fighting the URL. Everything else on the page
  // is unchanged, and with no context this is the page as it was.
  const [searchParams] = useSearchParams();
  const [sendFor] = useState(() => sendContextFromSearch(searchParams));
  const openedForHousehold = !isEmptySendContext(sendFor);

  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsError, setPropsError] = useState<string | null>(null);

  const [propertyId, setPropertyId] = useState(sendFor.propertyId ?? '');

  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  // Seeded too: the units effect below keeps a preselected unit when the loaded
  // list contains it, and falls back to the first unit when it does not.
  const [unitId, setUnitId] = useState(sendFor.unitId ?? '');

  const [residents, setResidents] = useState<ResidentRow[]>([]);
  const [residentsLoading, setResidentsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  // Flips true when the initiate route answers 503 (feature gated off).
  const [notEnabled, setNotEnabled] = useState(false);

  // Plan numbers. Principal is held as the raw dollars string the operator
  // types; cents are derived for the preview + send.
  const [principalInput, setPrincipalInput] = useState('');
  const [numberOfPayments, setNumberOfPayments] = useState(3);
  const [firstChargeDate, setFirstChargeDate] = useState('');

  // TYPE-ADAPTIVE principal: what the plan is for. Defaults to the security
  // deposit (the original behavior). 'other' reveals a required freeform label.
  const [principalType, setPrincipalType] = useState<PaymentPlanPrincipalType>('security_deposit');
  const [otherLabel, setOtherLabel] = useState('');

  // Lease start date — the occupancy's current-lease "lease dated ___" that
  // renders into the agreement. Prefilled from the mirror when available;
  // REQUIRED, so a generated agreement never has a blank date. `leaseDateOnFile`
  // tracks whether AppFolio supplied one (drives the "not on file" hint).
  const [leaseStartDate, setLeaseStartDate] = useState('');
  const [leaseDateOnFile, setLeaseDateOnFile] = useState(true);

  const principalCents = useMemo(() => parseMoneyDollarsToCents(principalInput), [principalInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listProperties();
        if (!cancelled) setProperties(list);
      } catch (err) {
        if (!cancelled) setPropsError(err instanceof Error ? err.message : 'Failed to load properties');
      } finally {
        if (!cancelled) setPropsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load units whenever the property changes. Reset the roster — it's unit-scoped.
  useEffect(() => {
    setResidents([]);
    setLoaded(false);
    if (!propertyId) {
      setUnits([]);
      setUnitId('');
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setUnitsError(null);
    listUnits(propertyId)
      .then((list) => {
        if (cancelled) return;
        setUnits(list);
        setUnitId((prev) => (list.some((u) => u.unitId === prev) ? prev : (list[0]?.unitId ?? '')));
      })
      .catch((err) => {
        if (!cancelled) setUnitsError(err instanceof Error ? err.message : 'Failed to load units');
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  // Changing the unit invalidates a previously-loaded roster + lease date.
  useEffect(() => {
    setResidents([]);
    setLoaded(false);
    setLeaseStartDate('');
    setLeaseDateOnFile(true);
  }, [unitId]);

  const updateResident = (key: number, patch: Partial<PaymentPlanResidentDraft>) => {
    setResidents((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const handleLoadResidents = async () => {
    if (!propertyId || !unitId) return;
    setResidentsLoading(true);
    try {
      const { residents: list, leaseStartDate: resolved } = await listCurrentResidents(
        propertyId,
        unitId,
      );
      setResidents(list.map(fromAppFolio));
      // Prefill the required lease-date field from the mirror. When AppFolio has
      // none on file, leave it blank + flag it so the field shows the "please
      // enter it" hint and gates Send until staff fill it in.
      setLeaseStartDate(resolved);
      setLeaseDateOnFile(!!resolved);
      setLoaded(true);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not load residents (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not load current residents';
      toast.error(msg, { title: 'Load residents' });
    } finally {
      setResidentsLoading(false);
    }
  };

  // Live schedule preview — cents-exact, matches the backend send (only when the
  // plan numbers validate, so we never preview a malformed schedule).
  const planValidationError = validatePaymentPlanInput({
    principalCents,
    numberOfPayments,
    firstChargeDate,
  });
  const schedule = useMemo(() => {
    if (planValidationError !== null) return [];
    return computePaymentScheduleCents({ principalCents, numberOfPayments, firstChargeDate });
  }, [planValidationError, principalCents, numberOfPayments, firstChargeDate]);

  const draft = {
    propertyId,
    unitId,
    residents,
    input: {
      principalCents,
      numberOfPayments,
      firstChargeDate,
      principalType,
      // Only meaningful for 'other'; canSendPaymentPlan requires it non-empty there.
      ...(principalType === 'other' ? { otherLabel: otherLabel.trim() } : {}),
    },
  };
  const validResidents = residents.filter(isResidentRowValid);
  // The lease start date is REQUIRED — a generated agreement must never carry a
  // blank "lease dated ___". Gate Send on a non-empty value (alongside the
  // existing roster + plan validation).
  const leaseDateValid = leaseStartDate.trim().length > 0;
  const canSend = !busy && leaseDateValid && canSendPaymentPlan(draft);

  const handleSend = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      const result = await initiatePaymentPlan({
        propertyId,
        unitId,
        residents: validResidents.map((r) => ({
          tenantId: r.tenantId,
          firstName: r.firstName.trim(),
          lastName: r.lastName.trim(),
          email: r.email.trim(),
        })),
        principalCents,
        numberOfPayments,
        firstChargeDate,
        principalType,
        // Send the freeform label only for the 'other' type (trimmed).
        ...(principalType === 'other' ? { otherLabel: otherLabel.trim() } : {}),
        leaseStartDate: leaseStartDate.trim(),
      });
      const n = result.sent;
      toast.success(
        `Sent the payment plan to ${n} ${n === 1 ? 'person' : 'people'} for signature.`,
        { title: 'Payment plan' },
      );
    } catch (err) {
      if (err instanceof PaymentPlanNotEnabledError) {
        setNotEnabled(true);
      } else {
        // If the backend still rejects for a missing lease date (e.g. the field
        // was cleared between load and send), flip the field into its "required"
        // state so the inline hint reappears alongside the toast.
        if (
          err instanceof ApiError &&
          (err.body as { code?: string } | null)?.code === 'lease_start_date_required'
        ) {
          setLeaseDateOnFile(false);
        }
        const msg =
          err instanceof ApiError
            ? ((err.body as { error?: string } | null)?.error ?? `Could not send the payment plan (HTTP ${err.status})`)
            : err instanceof Error
              ? err.message
              : 'Could not send the payment plan';
        toast.error(msg, { title: 'Payment plan' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Payment plan
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Send a standalone Security Deposit Payment Plan agreement to a unit&apos;s current residents.
          Pick the property and unit, load the current residents, edit the roster if needed, then enter
          the plan numbers. The schedule preview below matches exactly what gets sent. Residents sign
          first, then WHP countersigns.
        </p>
      </div>

      {openedForHousehold && (
        <div className="alert" data-tone="info" role="note" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            <div className="alert-title">Sending for {sendFor.name ?? 'this household'}</div>
            {sendFor.where ? <div>{sendFor.where}</div> : null}
            <div>Their property and unit are selected below — change either if that is not right.</div>
          </div>
        </div>
      )}

      {propsError && (
        <div className="alert" data-tone="danger" role="alert" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{propsError}</div>
        </div>
      )}

      {notEnabled && (
        <div className="alert" data-tone="info" role="note" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            <div className="alert-title">Sending isn&apos;t enabled yet</div>
            The Payment Plan agreement is a legal document. Once it&apos;s reviewed and the feature is
            turned on (<code>PAYMENT_PLAN_SEND_ENABLED</code>), the Send button below goes live.
          </div>
        </div>
      )}

      <div className="stack">
        {/* Property + unit picker */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Property &amp; unit</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="prop-select">Property</label>
              <SearchSelect
                id="prop-select"
                options={properties.map((p) => ({
                  value: p.propertyId,
                  label: p.name,
                  sublabel: p.address,
                }))}
                value={propertyId}
                onChange={setPropertyId}
                disabled={propsLoading}
                placeholder={propsLoading ? 'Loading…' : 'Select or search by name or address…'}
                emptyText="No matching properties"
              />
            </div>
            <div className="field">
              <label htmlFor="unit-select">Unit</label>
              <select
                id="unit-select"
                className="input"
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                disabled={!propertyId || unitsLoading}
              >
                <option value="">
                  {!propertyId
                    ? 'Select a property first…'
                    : unitsLoading
                      ? 'Loading units…'
                      : units.length === 0
                        ? 'No units found for this property'
                        : 'Select a unit…'}
                </option>
                {units.map((u) => (
                  <option key={u.unitId} value={u.unitId}>
                    {unitOptionLabel(u)}
                    {u.address ? ` — ${u.address}` : ''}
                  </option>
                ))}
              </select>
              {unitsError && <span className="helper" data-state="error">{unitsError}</span>}
            </div>
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              onClick={() => void handleLoadResidents()}
              disabled={!propertyId || !unitId || residentsLoading}
              style={{ alignSelf: 'flex-start' }}
            >
              {residentsLoading ? 'Loading residents…' : 'Load current residents'}
            </button>
          </div>
        </section>

        {/* Roster — editable, pre-filled from AppFolio */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Residents (signers)</h3>
          </div>
          {!loaded ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick a property + unit and choose “Load current residents” to pull the current tenants.
            </p>
          ) : (
            <>
              {residents.length === 0 && (
                <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-3)' }}>
                  <div className="alert-body">
                    AppFolio shows no current residents for this unit. You can add signers manually below.
                  </div>
                </div>
              )}
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
                Pre-filled from AppFolio (editable). Each resident needs a valid email to receive the
                signing request. WHP countersigns automatically after the residents sign.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {residents.map((r, idx) => {
                  const emailInvalid = isResidentEmailInvalid(r);
                  const emailEmpty = !r.email.trim();
                  const badge = tenantTypeLabel(r.tenantType);
                  return (
                    <div key={r.key} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="text"
                          className="input"
                          placeholder={`Resident ${idx + 1} first name`}
                          value={r.firstName}
                          onChange={(e) => updateResident(r.key, { firstName: e.target.value })}
                          aria-label={`Resident ${idx + 1} first name`}
                        />
                        {badge && (
                          <span className="pill" data-tone="neutral" style={{ alignSelf: 'flex-start' }}>
                            {badge}
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        className="input"
                        style={{ flex: 1 }}
                        placeholder={`Resident ${idx + 1} last name`}
                        value={r.lastName}
                        onChange={(e) => updateResident(r.key, { lastName: e.target.value })}
                        aria-label={`Resident ${idx + 1} last name`}
                      />
                      <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="email"
                          className="input"
                          placeholder="resident@example.com"
                          value={r.email}
                          onChange={(e) => updateResident(r.key, { email: e.target.value })}
                          aria-label={`Resident ${idx + 1} email`}
                          aria-invalid={emailInvalid}
                        />
                        {emailInvalid && <span className="helper" data-state="error">Enter a valid email.</span>}
                        {emailEmpty && <span className="helper" data-state="error">Email required to send.</span>}
                      </div>
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        data-icon-only
                        aria-label={`Remove resident ${idx + 1}`}
                        onClick={() => setResidents((prev) => prev.filter((x) => x.key !== r.key))}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => setResidents((prev) => [...prev, blankRow()])}
                  style={{ alignSelf: 'flex-start' }}
                >
                  + Add resident
                </button>
              </div>
            </>
          )}
        </section>

        {/* Plan numbers */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Plan</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="pp-principal-type">What is this plan for?</label>
              <select
                id="pp-principal-type"
                className="input"
                value={principalType}
                onChange={(e) => setPrincipalType(e.target.value as PaymentPlanPrincipalType)}
              >
                <option value="security_deposit">Security Deposit</option>
                <option value="rent">Rent</option>
                <option value="move_in_charges">Move-in charges</option>
                <option value="other">Other</option>
              </select>
              <span className="helper">
                Sets the agreement&apos;s title and wording. The Security Deposit option is the
                original, legally reviewed agreement; the other types are awaiting legal review.
              </span>
            </div>
            {principalType === 'other' && (
              <div className="field">
                <label htmlFor="pp-other-label">Describe what this plan covers</label>
                <input
                  id="pp-other-label"
                  type="text"
                  className="input"
                  placeholder="e.g. Pet Deposit"
                  value={otherLabel}
                  onChange={(e) => setOtherLabel(e.target.value)}
                  aria-invalid={!otherLabel.trim()}
                  aria-describedby="pp-other-label-hint"
                />
                <span
                  id="pp-other-label-hint"
                  className="helper"
                  data-state={!otherLabel.trim() ? 'error' : undefined}
                >
                  {otherLabel.trim()
                    ? `The agreement will be titled “${otherLabel.trim()} Payment Plan”.`
                    : 'Required — this becomes the agreement title (“… Payment Plan”).'}
                </span>
              </div>
            )}
            <div className="field">
              <label htmlFor="pp-principal">Principal</label>
              <input
                id="pp-principal"
                type="text"
                inputMode="decimal"
                className="input"
                placeholder="$1,300.00"
                value={principalInput}
                onChange={(e) => setPrincipalInput(e.target.value)}
              />
              <span className="helper">The total balance financed, split into equal monthly payments.</span>
            </div>
            <div className="field">
              <label htmlFor="pp-count">Number of payments</label>
              <select
                id="pp-count"
                className="input"
                value={numberOfPayments}
                onChange={(e) => setNumberOfPayments(Number(e.target.value))}
              >
                {Array.from(
                  { length: PAYMENT_PLAN_MAX_PAYMENTS - PAYMENT_PLAN_MIN_PAYMENTS + 1 },
                  (_, i) => PAYMENT_PLAN_MIN_PAYMENTS + i,
                ).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="pp-start">Start date (first charge)</label>
              <input
                id="pp-start"
                type="date"
                className="input"
                value={firstChargeDate}
                onChange={(e) => setFirstChargeDate(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="pp-lease-date">Lease start date</label>
              <input
                id="pp-lease-date"
                type="date"
                className="input"
                value={leaseStartDate}
                onChange={(e) => {
                  setLeaseStartDate(e.target.value);
                  if (e.target.value) setLeaseDateOnFile(true);
                }}
                aria-invalid={!leaseDateValid}
                aria-describedby="pp-lease-date-hint"
              />
              <span
                id="pp-lease-date-hint"
                className="helper"
                data-state={!leaseDateValid ? 'error' : undefined}
              >
                {leaseDateOnFile && leaseStartDate
                  ? 'Prefilled from AppFolio — edit if the lease was dated differently. This becomes the “lease dated …” in the agreement.'
                  : 'Not on file in AppFolio — please enter the lease start date. The agreement can’t be sent with a blank date.'}
              </span>
            </div>
          </div>
        </section>

        {/* Schedule preview */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Schedule preview</h3>
          </div>
          {schedule.length === 0 ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {principalCents <= 0 || !firstChargeDate
                ? 'Enter a principal amount and a start date to preview the schedule.'
                : (planValidationError ?? 'Enter the plan numbers to preview the schedule.')}
            </p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Charge Date</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((p) => (
                    <tr key={p.paymentNumber}>
                      <td>{formatChargeDate(p.chargeDate)}</td>
                      <td style={{ textAlign: 'right' }}>{formatMoneyCents(p.amount)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 'var(--weight-bold)' }}>Total</td>
                    <td style={{ textAlign: 'right', fontWeight: 'var(--weight-bold)' }}>
                      {formatMoneyCents(principalCents)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Action */}
        <section className="card">
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Sends the payment-plan agreement to the residents above for signature. WHP countersigns
              last. Track it on the Requests page.
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => void handleSend()}
              disabled={!canSend}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
            >
              {busy
                ? 'Sending…'
                : `Send payment plan${validResidents.length > 0 ? ` to ${validResidents.length} →` : ' →'}`}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
